import { isArray } from "ArrayUtils";
import type { Entity, EntityType } from "BreezeExtensions";
import type { GetterOptions } from "DependencyOptions";
import {
  type DependencyResult,
  getAvailableResult,
  getNotAvailableResult,
  getNotLoadedResult,
  getNullAvailableResult,
} from "DependencyResult";
import type {
  DependencyAccessMixedValue,
  DependencyArgument,
  DependencyVisitorContext,
} from "DependencyVisitorContext";
import { areValuesEqual } from "DependencyVisitorUtils";
import { DependencyError } from "Errors";
import glowMacroProvider from "GlowMacroProvider";
import { recursiveUnwrap } from "KnockoutExtensions";
import log from "Log";
import { State } from "StateConstants";
import breeze from "breeze-client";
import ko from "knockout";

function macroPath<Mode>(
  propertyPath: string,
  items: DependencyArgument[],
  _data: unknown,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, unknown> {
  const data = getAvailableResult(glowMacroProvider.getGlowMacroEntity());
  return context.strategy.wait(reducePath(items, 0, data, context), (result) => {
    if (context.options.onVisitMacro) {
      context.options.onVisitMacro(propertyPath, result.state);
    }
    return result;
  });
}

function path<Mode>(
  items: DependencyArgument[],
  data: unknown[],
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, unknown> {
  return reducePath(items, 0, getAvailableResult(data), context);
}

function rootPath<Mode>(
  items: DependencyArgument[],
  _data: unknown,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, unknown> {
  return reducePath(items, 0, getAvailableResult(context.rootData), context);
}

function reducePath<Mode>(
  items: DependencyArgument[],
  itemIndex: number,
  result: DependencyResult,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, unknown> {
  if (itemIndex === items.length || result.state !== State.Available) {
    return result;
  }

  const awaitable = items[itemIndex](result.value, context);
  return context.strategy.wait(awaitable, (result) => reducePath(items, itemIndex + 1, result, context));
}

function isDefault<Mode>(
  value: DependencyArgument,
  propertyName: string,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, boolean> {
  if (data == null) {
    return getAvailableResult(true);
  }

  return context.strategy.waitAvailableOne(value, data, context, (value) => {
    if (isArray(value)) {
      throw new DependencyError("IsDefault() can only be used on a scalar property.");
    } else {
      const defaultValue = getDefaultValue(data, propertyName);
      return getAvailableResult(areValuesEqual(value, defaultValue));
    }
  });
}

function getDefaultValue(data: unknown, propertyName: string): unknown {
  if (!isEntity(data)) {
    return undefined;
  }

  const { entityType } = data;
  const property = entityType.getProperty(propertyName);
  if (property && (property as MaybeExtendedProperty).defaultValue !== undefined) {
    return (property as MaybeExtendedProperty).defaultValue;
  }

  if (!property || property.isDataProperty) {
    return entityType.getEmptyValue?.(propertyName, true);
  }

  return undefined;
}

function property<Mode>(
  propertyName: string,
  separator: string | undefined,
  isTopLevel: boolean,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, unknown> {
  if (isTopLevel && !separator) {
    if (context.options.skipLastSegmentEvaluation) {
      return getAvailableResult(data);
    } else if (context.options.valueToSet !== undefined) {
      return setPropertyValue(propertyName, data, context);
    }
  }

  return getPropertyValue(propertyName, separator, isTopLevel, data, context);
}

function getPropertyValue<Mode>(
  propertyName: string,
  separator: string | undefined,
  isTopLevel: boolean,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, unknown> {
  if (!isArray(data)) {
    return getPropertyValueForItem(propertyName, separator, isTopLevel, data, context);
  }

  return context.strategy.map(
    data,
    (item) => getPropertyValueForItem(propertyName, separator, isTopLevel, item, context),
    (items) => {
      const result = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.state === State.NotLoaded) {
          return item;
        } else if (item.state === State.Available) {
          if (isArray(item.value)) {
            result.push(...item.value);
          } else {
            result.push(item.value);
          }
        }
      }
      return getAvailableResult(result);
    },
  );
}

export function getPropertyValueForItem<Mode>(
  propertyName: string,
  separator: string | undefined,
  isTopLevel: boolean,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, unknown> {
  if (data == null) {
    return getAvailableResult(null);
  }

  let propertyValue = (data as Record<string, unknown>)[propertyName];
  if (propertyValue === undefined && context.options.throwOnError) {
    throw new DependencyError(`Property '${getPropertyDescription(propertyName, data)}' does not exist.`);
  }

  if (context.options.onVisitProperty) {
    context.options.onVisitProperty(data, propertyName);
  }

  if (propertyValue == null) {
    return getAvailableResult(null);
  }

  const isSlashSeparator = separator === "/";
  const currentItemAccessor = isSlashSeparator && context.options.currentItemAccessor;
  let state = getPropertyState(propertyValue, context.options);

  if (state === State.NotLoaded) {
    if (context.shouldAvoidLoading()) {
      return getNotLoadedResult();
    } else if (currentItemAccessor && !currentItemAccessor.shouldLoad(data, propertyName)) {
      state = State.Available;
    } else {
      let promise: Promise<void> | undefined;
      if (currentItemAccessor) {
        promise = currentItemAccessor.loadAsync(data, propertyName);
      } else {
        promise = (propertyValue as MaybeExtendedProperty).loadAsync?.();
      }

      // TODO: Refactor to remove the type assertion.
      (propertyValue as () => unknown)(); // Subscribe to value.
      return context.strategy.wait(promise, () => {
        state = (propertyValue as MaybeExtendedProperty).getState!();
        if (state === State.NotLoaded) {
          return getNotLoadedResult();
        } else {
          return getLoadedResult();
        }
      });
    }
  }

  return getLoadedResult();

  function getLoadedResult(): DependencyResult<unknown | null> {
    if (state === State.NotAvailable) {
      return getPropertyNotAvailableResult(propertyName, data);
    } else if (currentItemAccessor) {
      propertyValue = currentItemAccessor.read(data, propertyName);
    } else if (separator || !isTopLevel || context.options.unwrapFinalValue !== false) {
      propertyValue = recursiveUnwrap(propertyValue, 0);
    }

    return state === State.Available ? getAvailableResult(propertyValue ?? null) : getNotLoadedResult();
  }
}

function getPropertyDescription(propertyName: string, data: unknown): string {
  return isEntity(data) ? data.entityType.interfaceName + "." + propertyName : propertyName;
}

function getPropertyNotAvailableResult(propertyName: string, data: unknown): DependencyResult {
  log.warning(`Property '${getPropertyDescription(propertyName, data)}' is not available.`);
  return getNotAvailableResult();
}

function setPropertyValue<Mode>(
  propertyName: string,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, unknown> {
  let promise: Promise<unknown> | undefined;
  if (isArray(data)) {
    promise = Promise.all(data.map((item) => setPropertyValueCore(propertyName, item, context)));
  } else {
    promise = setPropertyValueCore(propertyName, data, context);
  }

  return context.strategy.wait(promise, getNullAvailableResult);
}

function setPropertyValueCore<Mode>(
  propertyName: string,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): undefined | Promise<void> {
  if (!data) {
    return undefined;
  }

  const propertyValue = (data as Record<string, unknown>)[propertyName];
  if (propertyValue === undefined && context.options.throwOnError) {
    throw new DependencyError(`Property '${getPropertyDescription(propertyName, data)}' does not exist.`);
  }

  const { valueToSet } = context.options;
  if (ko.isObservable(propertyValue)) {
    if (isEntity(data)) {
      if (context.options.setDefaultValues) {
        return (breeze as unknown as ExtendedBreeze).utils.runWithProposedValuesAsync(data, () => {
          return data.entityAspect.setValueAsync(propertyName, valueToSet);
        });
      } else {
        return data.entityAspect.setValueAsync(propertyName, valueToSet);
      }
    } else {
      propertyValue(valueToSet);
    }
  } else {
    (data as Record<string, unknown>)[propertyName] = valueToSet;
  }

  return undefined;
}

function getPropertyState(propertyValue: MaybeExtendedProperty, options: GetterOptions): State {
  if (options.observeState && propertyValue.observeState) {
    return propertyValue.observeState();
  } else if (propertyValue.getState) {
    return propertyValue.getState();
  } else {
    return State.Available;
  }
}

function ofType(typeName: string, data: unknown): DependencyResult {
  const match = isEntity(data) && matchesTypeName(data.entityType, typeName);
  return getAvailableResult(match ? data : null);
}

function matchesTypeName(entityType: EntityType | undefined, typeName: string): boolean {
  return (
    !!entityType &&
    (entityType.interfaceName === typeName || matchesTypeName(entityType.baseEntityType as EntityType, typeName))
  );
}

function originalValue<Mode>(
  propertyName: string,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): DependencyResult {
  if (data == null) {
    return getAvailableResult(null);
  }

  if (!isEntity(data)) {
    if (isArray(data)) {
      throw new DependencyError("OriginalValue() can only be used on a scalar property.");
    } else {
      throw new DependencyError(
        `OriginalValue() cannot be used on property '${propertyName}' because the owner is not an entity.`,
      );
    }
  }

  const propertyValue = (data as unknown as Record<string, unknown>)[propertyName];
  if (propertyValue !== undefined) {
    if (!data.entityType.getDataProperty(propertyName)) {
      throw new DependencyError(
        `OriginalValue() cannot be used on property '${data.entityType.interfaceName}.${propertyName}' because it is not a data property.`,
      );
    } else if (data.entityAspect.entityState.isAdded()) {
      return getAvailableResult(null);
    } else if ((propertyValue as MaybeExtendedProperty)!.getState!() === State.Available) {
      return getAvailableResult(data.entityAspect.getOriginalValue(propertyName));
    } else {
      // Data property state can only be Available or NotAvailable.
      return getPropertyNotAvailableResult(propertyName, data);
    }
  } else if (context.options.throwOnError) {
    throw new DependencyError(`Property '${getPropertyDescription(propertyName, data)}' does not exist.`);
  } else {
    return getAvailableResult(null);
  }
}

// TODO: Remove this and switch from BreezeExtensions to breeze-client.
function isEntity(data: unknown): data is Entity {
  return !!data && typeof data === "object" && "entityType" in data;
}

// TODO: Use EntityRuleExtensions.enableProposedValuesAsync but need to break the circular dependency.
interface ExtendedBreeze {
  utils: {
    runWithProposedValuesAsync(entity: Entity, action: () => Promise<void>): Promise<void>;
  };
}

// TODO: Remove when we refactor & extract code from BreezeInit.
interface MaybeExtendedProperty {
  defaultValue?: unknown;
  getState?(): State;
  loadAsync?(): Promise<void>;
  observeState?(): State;
}

export default { path, isDefault, macroPath, ofType, originalValue, property, rootPath };
